<script setup>
import { ref, computed } from 'vue';
import { useOrderStore } from '@/stores/OrderStore';
import { useAuthStore } from '@/stores/authStore';

const orderStore = useOrderStore();
const authStore = useAuthStore();
const emit = defineEmits(['close']);

const commentText = ref('');
const isPrintable = ref(false);
const selectedItemId = ref('');

const orderItems = computed(() => orderStore.state.orderItems);
const notes = computed(() => orderStore.state.orderNotes);

const itemLabel = (item, index) => {
  const hasDupe = orderItems.value.filter(i => i.Sku === item.Sku).length > 1;
  return hasDupe ? `${item.ItemName} #${index + 1} — ${item.Sku}` : `${item.ItemName} — ${item.Sku}`;
};

const submit = () => {
  if (!commentText.value.trim()) return;
  let text = commentText.value.trim();
  if (selectedItemId.value) {
    const prefix = isPrintable.value ? 'printable' : 'item';
    text = `${prefix}:${selectedItemId.value}:${text}`;
  }
  orderStore.addOrderNote(text);
  commentText.value = '';
  isPrintable.value = false;
  selectedItemId.value = '';
};

const parsedNote = (text) => {
  if (text.startsWith('printable:') || text.startsWith('item:')) {
    const printable = text.startsWith('printable:');
    const parts = text.split(':');
    const id = parts[1] || '';
    const comment = parts.slice(2).join(':');
    const matchedItem = orderItems.value.find(i => (i.uniqueId || i.Sku) === id);
    const label = matchedItem ? matchedItem.ItemName : id;
    return { printable, id, label, comment };
  }
  return { printable: false, id: '', label: '', comment: text };
};
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-50">
        <Transition name="modal-inner">
          <div class="fixed top-10 z-50 flex items-start justify-center w-10/12 max-h-[85vh] overflow-y-auto">
            <div class="bg-white rounded-2xl p-6 w-full shadow-lg">

              <!-- Header -->
              <div class="flex justify-between items-center mb-5">
                <h3 class="text-lg font-semibold">Order Comments</h3>
                <button @click="emit('close')" class="px-3 py-2 bg-gray-200 rounded-lg">
                  <i class="pi pi-times"></i>
                </button>
              </div>

              <!-- Add comment form -->
              <div class="bg-[#f4f5f7] rounded-2xl p-4 mb-5">
                <textarea
                  v-model="commentText"
                  rows="3"
                  class="w-full border rounded-lg p-2 mb-3 bg-white"
                  placeholder="Enter comment..."
                ></textarea>

                <!-- Printable item toggle -->
                <label class="flex items-center gap-2 cursor-pointer mb-3 select-none">
                  <input
                    type="checkbox"
                    v-model="isPrintable"
                    class="w-4 h-4 accent-purple-500"
                  />
                  <span class="text-sm font-medium">Printable item comment</span>
                </label>

                <!-- Item selector (optional) -->
                <div class="mb-3">
                  <label class="text-sm text-gray-500 mb-1 block">Assign to item (SKU)</label>
                  <select
                    v-model="selectedItemId"
                    class="w-full border rounded-lg p-2 bg-white text-sm"
                  >
                    <option value="" disabled>Select an item...</option>
                    <option
                      v-for="(item, index) in orderItems"
                      :key="item.uniqueId || item.Sku"
                      :value="item.uniqueId || item.Sku"
                    >
                      {{ itemLabel(item, index) }}
                    </option>
                  </select>
                </div>

                <div class="flex justify-end">
                  <button
                    @click="submit"
                    :disabled="!commentText.trim() || (isPrintable && !selectedItemId)"
                    class="px-4 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
                  >
                    <i class="pi pi-plus"></i> Add Comment
                  </button>
                </div>
              </div>

              <!-- Saved notes list -->
              <div v-if="notes.length === 0" class="text-center text-gray-400 text-sm py-4">
                No comments yet.
              </div>

              <div
                v-for="(note, index) in notes"
                :key="index"
                class="mb-3 bg-[#f4f5f7] rounded-xl p-4 flex justify-between items-start gap-4"
              >
                <div class="flex-1">
                  <template v-if="parsedNote(note.text).id">
                    <span
                      class="inline-block text-xs font-semibold rounded px-2 py-0.5 mb-1"
                      :class="parsedNote(note.text).printable ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-600'"
                    >
                      {{ parsedNote(note.text).printable ? 'Printable' : 'Item note' }} · {{ parsedNote(note.text).label }}
                    </span>
                    <p class="text-sm">{{ parsedNote(note.text).comment }}</p>
                  </template>
                  <p v-else class="text-sm">{{ note.text }}</p>
                  <p class="text-xs text-gray-400 mt-1">{{ orderStore.formatDate(note.timestamp) }}</p>
                </div>
                <button
                  @click="orderStore.removeOrderNote(index)"
                  class="px-3 py-2 bg-gray-200 rounded-lg text-gray-600 hover:bg-red-100 hover:text-red-500 transition"
                >
                  <i class="pi pi-trash"></i>
                </button>
              </div>

            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}
.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}
.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}
.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}
.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}
.modal-inner-leave-to {
  transform: scale(0.8);
}
</style>
